/**
 * Copy and destinations for the public Fias marketing site.
 *
 * Kept in one module because two surfaces consume it: the React pages, and the
 * build-time prerender that turns them into crawlable HTML. Marketing copy
 * lives here rather than inline so the SEO description and the page's own
 * words can't drift apart.
 */

import { normalizeCdnBaseUrl, SITE_MEDIA_VERSION } from '@fias/platform-resources';
import { PUBLIC_SITE_LINKS } from '@fias/public-site-ui';

/**
 * Base URL of the platform-resources CDN, where the site's large media lives.
 *
 * Inlined by Vite at build time, so it is fixed in the prerendered snapshot too.
 * Empty when the CDN is unconfigured (local dev, or an environment deployed
 * before PlatformResourcesCdnStack) — consumers must treat that as "no media"
 * and degrade, exactly as the font client does, rather than emitting a URL that
 * 404s.
 */
const CDN_BASE = normalizeCdnBaseUrl(
  import.meta.env.VITE_PLATFORM_RESOURCES_CDN_DOMAIN as string | undefined,
);

/** Canonical path of the marketing arche. */
export const SITE_ARCHE_PATH = '/a/fias-site';

/**
 * Where the site is mounted. The same components render at the domain root
 * (for signed-out visitors) and at {@link SITE_ARCHE_PATH}, and internal links
 * have to resolve relative to whichever one is in the address bar.
 */
export type SiteBasePath = '/' | typeof SITE_ARCHE_PATH;

/** Default base path — the arche's own canonical URL. */
export const SITE_BASE_PATH: SiteBasePath = SITE_ARCHE_PATH;

export const SUPPORT_EMAIL = 'support@fias.io';

/** Closed-beta application lives in its own arche. */
export const BETA_PATH = PUBLIC_SITE_LINKS.join;

/** Every game on Fias — the public Games page (ADR-035). */
export const GAMES_PATH = PUBLIC_SITE_LINKS.games;

/** Platform documentation lives in the Learn About Fias arche. */
export const DOCS_PATH = PUBLIC_SITE_LINKS.docs;

/** `icon` names the mark to render; the label is the accessible name. */
export const SOCIAL_LINKS = [
  { label: 'X', icon: 'x', href: 'https://x.com/tomlucient' },
  { label: 'LinkedIn', icon: 'linkedin', href: 'https://linkedin.com/fiasai' },
  { label: 'YouTube', icon: 'youtube', href: 'https://www.youtube.com/@tomlucient' },
] as const;

/** Hero. */
export const HERO = {
  eyebrow: 'Next Gen AI',
  headline: "The world's platform for AI monetization",
  subhead: 'Revolutionizing how AI creates and captures value in the digital economy.',
} as const;

/**
 * The two positioning statements directly under the hero. Each sits beneath a
 * product mock that shows the claim in the product, so the copy only has to
 * name it.
 */
export const PILLARS = [
  {
    title: 'Easily Build and Deploy Your Own Applications',
    body: 'Anyone can quickly build their own applications and deploy them into the Fias marketplace to immediately start generating revenue around your expertise or your ideas. Include in-app purchases, subscriptions, and payments in your apps.',
  },
  {
    title: 'All Your Applications and Data in One Place',
    body: 'One location for all of your applications and data. Find applications to manage your personal life, business activities, or even play games. And all of the applications and data are all connected through Fias AI, your personal assistant.',
  },
] as const;

/**
 * Problem and solution, presented as a facing pair. Each carries a `label`
 * rendered above the title on its own line, the way the current site sets them.
 */
export const PROBLEM = {
  label: 'The problem:',
  title: 'Rapid Innovation, Limited Monetization',
  body: 'The biggest challenge in AI right now is monetization. There is no unified, efficient platform enabling developers to monetize their AI applications and autonomous AI agents. Even on platforms where you can build your own applications, you cannot easily sell them, and you cannot easily coordinate between different applications and all of your data.',
} as const;

export const SOLUTION = {
  label: 'The solution:',
  title:
    'An Integrated Marketplace, Sophisticated Orchestration, and Instant Payments, all with Security and Privacy.',
  body: 'In contrast, Fias allows for many AI companies and products to work together to solve problems faster, cheaper, and with better outcomes. Fias allows for sophisticated multi-party solutions, powerful data integrations, and seamless micropayment monetization across the entire ecosystem. The platform is founded on Security and Privacy.',
} as const;

/** Share-of-market figures for the problem panel's chart. */
export const DOMINANCE = {
  caption: 'Big Tech Dominates',
  monoliths: 81,
  others: 19,
  /** The names that scroll beneath the chart. */
  incumbents: ['ChatGPT', 'Gemini', 'Copilot', 'Claude', 'Grok', 'Llama', 'Perplexity'],
} as const;

/** Figures for the solution panel's utilization chart. */
export const UTILIZATION = {
  caption: 'Utilization',
  window: 'last 60 days',
  amount: '$80,376',
  entity: 'Image Verifier Entity',
} as const;

/**
 * Arche list shown in the second pillar's mock. Deliberately spans personal,
 * business, and play — the claim above the panel is that everything lives in
 * one place, so a list of four business tools would undercut it.
 */
export const SAMPLE_ARCHES = [
  'Email, Calendar, Contacts',
  'Risk Management',
  'Games',
  'Document Signatures',
] as const;

/**
 * The interface section. The two capability rows scroll in opposite
 * directions, so they read as one field of capability rather than a list.
 */
export const INTERFACE_SECTION = {
  eyebrow: { lead: 'More Features/', trail: 'Services' },
  heading: 'The Perfect Interface for Every Use Case',
  body: 'For AI to be truly useful in complex problems, a one-size fits all chat input is not the right solution for most needs.',
  rowOne: [
    'Smart Automation',
    'Data Insights',
    'Predictive AI',
    'Content AI',
    'Privacy',
    'Orchestration',
    'User Personalization',
  ],
  rowTwo: [
    'NLP Models',
    'AI Agents',
    'Scalable Systems',
    'Auto Scheduling',
    'Omnichannel AI',
    'API Support',
    'Data Security',
  ],
  closer: "Delivering Innovative AI solutions Tailored to Our User's Needs.",
} as const;

/** DevvX blockchain section. */
export const DEVVX = {
  eyebrow: { lead: 'AI Payments at ', trail: 'Massive Scale' },
  heading: 'The DevvX Blockchain Powers Fias Micropayments',
  features: [
    { title: 'High Throughput', detail: 'Millions of Transactions Per Second' },
    { title: 'Low Cost', detail: 'The Only Blockchain for AI Scale' },
  ],
  /**
   * The explainer, shown as a poster that opens a player on click rather than a
   * video that loads with the page. The bytes are only fetched once someone
   * asks for them, and the prerendered snapshot carries a poster and a button —
   * real markup a crawler can read, where a media element would be a hole.
   *
   * Self-hosted on the platform-resources CDN rather than committed to
   * `client/public`: see SITE_MEDIA_VERSION for why, and
   * `scripts/publish-site-media-to-cdn.ts` for how the bytes get there.
   * `src` is empty when the CDN is unconfigured; the page then renders the
   * poster with no play affordance rather than a button that leads nowhere.
   */
  video: {
    src: CDN_BASE ? `${CDN_BASE}/site-media/${SITE_MEDIA_VERSION}/devvx-blockchain-1080p.mp4` : '',
    title: 'DevvX Blockchain',
    /** Spoken in the play button's accessible name, so the length is known before committing. */
    durationLabel: '3 minutes 6 seconds',
    poster: '/fias-devvx-node.jpg',
    posterAlt: 'A DevvX blockchain node',
  },
} as const;

/**
 * What people tell us AI gets wrong today. Verbatim sentiment from the current
 * site — these set up the architecture section that answers them.
 */
export const VOICES_SECTION = {
  eyebrow: { lead: 'We Solve ', trail: "AI's Biggest Challenges" },
  heading: 'AI Will Fulfill Its Potential When It Can Solve the Tough Problems',
  quotes: [
    "I'm tired of AI lying, making things up, or being overconfident. I hate paying for subscriptions I don't touch for months, then I run out of credits when I need it. I'm annoyed when I spend hours getting no where. A better approach is needed!",
    'AI is amazing, but it still has a long way to go. Whenever I work on a problem that is difficult - something that is more than just asking a straightforward question - I find that it breaks down with complex problems.',
    'AI is fun to play with, but for it to make a difference for me in business, it needs to manage data and privacy, interface with the systems I use, and maintain consistency without errors on mission critical tasks.',
  ],
  answer:
    'Fias solves the big problems in AI. The architecture is built for the future, using smaller, specialized agentic models that are economically viable and highly coordinated to solve complex problems with accuracy and efficiency.',
} as const;

/**
 * Architecture features. Eight cards across two counter-scrolling rows, so the
 * order within each row is a presentation choice rather than a ranking.
 * `icon` names the mark each card carries — see ARCHITECTURE_ICONS in the page.
 */
export const ARCHITECTURE = {
  eyebrow: { lead: 'Architecture ', trail: 'Features' },
  heading: 'The Fias Architecture',
  body: 'Fias works differently than other platforms, with architectural improvements that give users the best experience and developers the best monetization capabilities.',
  rowOne: [
    {
      icon: 'key',
      title: 'Frictionless Access',
      body: 'AI developers gain direct access to customers seeking their specific technical capabilities, removing the barriers between building and earning.',
    },
    {
      icon: 'head',
      title: 'Human Capability',
      body: 'The system uses strategic human input where human judgment remains superior while allowing AI to handle the rest.',
    },
    {
      icon: 'code',
      title: 'Entities',
      body: 'The Fias platform combines humans, AI Agents, and programming blocks to solve problems where each part does what it does best.',
    },
    {
      icon: 'coins',
      title: 'Microtransactions',
      body: "Fias operates on transparent value exchange with instant micropayments paid upon any task's completion.",
    },
  ],
  rowTwo: [
    {
      icon: 'wallet',
      title: 'The Money of AI',
      body: 'While payments can be made in many formats, the Fias Cryptocurrency allows for seamless, low-cost, high-speed payments - The future Money of AI.',
    },
    {
      icon: 'handCoin',
      title: 'Only Pay For Your Needs',
      body: 'Most AI platforms have frustrating fees. You are locked in to monthly payments and still often run out of credits. With Fias, you only pay for what you need.',
    },
    {
      icon: 'shield',
      title: 'Verifications',
      body: 'Fias utilizes multiple AI systems to verify results of other AI systems, removing hallucinations and errors that cost enormous time.',
    },
    {
      icon: 'link',
      title: 'Collaboration',
      body: 'Extensive AI providers bring specialized and efficient models. Monolithic approaches will fade, as collaboration between specialized models wins.',
    },
  ],
} as const;

export const CLOSING = {
  eyebrow: { lead: 'Start ', trail: 'Earning Now' },
  heading: 'Unlock the Power of AI With Fias',
  body: "We're not just building a platform - we're establishing the infrastructure for the trillion-dollar AI economy that will reshape global commerce.",
  /** Text of the seal that rotates around the closing image. */
  seal: 'Monetization and Collaboration',
} as const;
